import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { error: Error | null }

// Top-level safety net. A thrown render error would otherwise unmount the whole
// React tree, leaving the bare (black) document body — so a single bad component
// took the entire site down. This catches it and shows a recoverable message.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0a0c10', color: '#e6edf3', fontFamily: 'JetBrains Mono, monospace', padding: 24,
      }}>
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <div style={{ color: '#ff3344', fontSize: 20, fontWeight: 700, marginBottom: 12, letterSpacing: '0.1em' }}>
            [ SOMETHING BROKE ]
          </div>
          <div style={{ fontSize: 13, color: '#9aa4b2', lineHeight: 1.7, marginBottom: 20 }}>
            The page hit an error and couldn't render. This has been logged to the console.
          </div>
          <pre style={{
            fontSize: 11, color: '#ffb300', background: '#11151c', border: '1px solid #232b36',
            borderRadius: 4, padding: 12, textAlign: 'left', overflowX: 'auto', marginBottom: 20,
          }}>{String(this.state.error?.message || this.state.error)}</pre>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            style={{
              fontFamily: 'inherit', fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer',
              color: '#00ff88', background: 'transparent', border: '1px solid rgba(0,255,136,0.5)',
              borderRadius: 4, padding: '8px 18px',
            }}
          >RELOAD</button>
        </div>
      </div>
    );
  }
}
